import { Permission } from '@act/shared/authorization';
import { lazy, ReactNode } from 'react';
import {
  Navigate,
  useNavigate,
  useParams,
  useSearchParams,
} from 'react-router-dom';
import { setManagerOperatingSystemGuideSearchParam } from '@act/react-components/manager-operating-system/managerOperatingSystemGuideSelection.util';
import {
  MANAGER_OPERATING_SYSTEM_FEATURE_FLAG,
  MANAGER_OPERATING_SYSTEM_GUIDE_PATH_PREFIX,
  MANAGER_OPERATING_SYSTEM_OUTCOME_PATH_PREFIX,
  MANAGER_OPERATING_SYSTEM_PREFIX_PATH,
  MANAGER_OPERATING_SYSTEM_TEAM_DETAILS_PATH,
  MANAGER_OPERATING_SYSTEM_TEAM_WORKFLOW_PATH,
  MANAGER_OPERATING_SYSTEM_YOUR_GUIDES_PATH,
} from '@act/react-components/manager-operating-system/managerOperatingSystem.constants';
import { actingUserAppSelectors } from 'redux/slices/actingUserSlice.slice';
import { useFeatureFlag } from 'redux/utils/feature-flags/featureFlagHook.hook';
import { Routes, SyncHook } from '../routeTypes';
import { DOCKET_PREFIX_PATH } from './DocketRoutes';

const ManagerOperatingSystemNavigationDrawer = lazy(() =>
  import(
    '@act/react-components/manager-operating-system/ManagerOperatingSystemNavigationDrawer'
  ).then(module => ({
    default: module.ManagerOperatingSystemNavigationDrawer,
  }))
);

const ManagerOperatingSystemTeamWorkflow = lazy(() =>
  import(
    '@act/react-components/manager-operating-system/ManagerOperatingSystemTeamWorkflow'
  ).then(module => ({
    default: module.ManagerOperatingSystemTeamWorkflow,
  }))
);

const ManagerOperatingSystemTeamDetails = lazy(() =>
  import(
    '@act/react-components/manager-operating-system/ManagerOperatingSystemTeamDetails'
  ).then(module => ({
    default: module.ManagerOperatingSystemTeamDetails,
  }))
);

const ManagerOperatingSystemOutcomeWorkflowPage = lazy(() =>
  import(
    '@act/react-components/manager-operating-system/ManagerOperatingSystemOutcomeWorkflowPage'
  ).then(module => ({
    default: module.ManagerOperatingSystemOutcomeWorkflowPage,
  }))
);

export {
  MANAGER_OPERATING_SYSTEM_PREFIX_PATH,
  MANAGER_OPERATING_SYSTEM_TEAM_WORKFLOW_PATH,
  MANAGER_OPERATING_SYSTEM_YOUR_GUIDES_PATH,
};

interface ManagerOperatingSystemPermissionGateProps {
  children: ReactNode;
}

/**
 * Prevents users without Manager Operating System access, or with the feature
 * flag off, from rendering those routes.
 * @param props - Protected route content.
 * @returns Manager Operating System content or a redirect to the guide docket.
 */
export const ManagerOperatingSystemPermissionGate = ({
  children,
}: ManagerOperatingSystemPermissionGateProps) => {
  const canAccessManagerOperatingSystem =
    actingUserAppSelectors.useUserHasPermission(
      Permission.CAN_ACCESS_MANAGER_OPERATING_SYSTEM
    );
  const isManagerOperatingSystemEnabled = useFeatureFlag(
    MANAGER_OPERATING_SYSTEM_FEATURE_FLAG
  );

  return canAccessManagerOperatingSystem &&
    isManagerOperatingSystemEnabled ? (
    <>{children}</>
  ) : (
    <Navigate to={DOCKET_PREFIX_PATH} replace />
  );
};

/**
 * Sends a MOS index path to Team Details, keeping MOS filters.
 * @returns A redirect to Team Details.
 */
const RedirectToTeamDetails = () => {
  const [searchParams] = useSearchParams();
  const search = searchParams.toString();
  return (
    <Navigate
      to={
        search
          ? `${MANAGER_OPERATING_SYSTEM_TEAM_DETAILS_PATH}?${search}`
          : MANAGER_OPERATING_SYSTEM_TEAM_DETAILS_PATH
      }
      replace
    />
  );
};

/**
 * Sends `/guide/:guideId` to Team Details with that guide selected.
 * @returns A redirect that keeps the other MOS query params.
 */
const RedirectGuideToTeamDetails = () => {
  const { guideId } = useParams();
  const [searchParams] = useSearchParams();
  const next = setManagerOperatingSystemGuideSearchParam(
    searchParams,
    guideId ?? null
  );
  const search = next.toString();
  return (
    <Navigate
      to={
        search
          ? `${MANAGER_OPERATING_SYSTEM_TEAM_DETAILS_PATH}?${search}`
          : MANAGER_OPERATING_SYSTEM_TEAM_DETAILS_PATH
      }
      replace
    />
  );
};

/**
 * Wraps every Manager Operating System route in the same permission and
 * feature-flag gate.
 * @param element - Route content.
 * @returns The gated route element.
 */
const gateManagerOperatingSystemRoute = (element: ReactNode) => (
  <ManagerOperatingSystemPermissionGate>
    {element}
  </ManagerOperatingSystemPermissionGate>
);

const routes: Routes = [
  {
    path: MANAGER_OPERATING_SYSTEM_YOUR_GUIDES_PATH,
    element: gateManagerOperatingSystemRoute(<RedirectToTeamDetails />),
  },
  {
    path: MANAGER_OPERATING_SYSTEM_TEAM_WORKFLOW_PATH,
    element: gateManagerOperatingSystemRoute(
      <ManagerOperatingSystemNavigationDrawer>
        <ManagerOperatingSystemTeamWorkflow />
      </ManagerOperatingSystemNavigationDrawer>
    ),
  },
  {
    path: MANAGER_OPERATING_SYSTEM_TEAM_DETAILS_PATH,
    element: gateManagerOperatingSystemRoute(
      <ManagerOperatingSystemNavigationDrawer>
        <ManagerOperatingSystemTeamDetails />
      </ManagerOperatingSystemNavigationDrawer>
    ),
  },
  {
    path: `${MANAGER_OPERATING_SYSTEM_OUTCOME_PATH_PREFIX}/:outcomeId`,
    element: gateManagerOperatingSystemRoute(
      <ManagerOperatingSystemNavigationDrawer>
        <ManagerOperatingSystemOutcomeWorkflowPage />
      </ManagerOperatingSystemNavigationDrawer>
    ),
  },
  {
    path: MANAGER_OPERATING_SYSTEM_PREFIX_PATH,
    element: gateManagerOperatingSystemRoute(<RedirectToTeamDetails />),
  },
  {
    path: `${MANAGER_OPERATING_SYSTEM_GUIDE_PATH_PREFIX}/:guideId`,
    element: gateManagerOperatingSystemRoute(<RedirectGuideToTeamDetails />),
  },
];

const useSyncHook: SyncHook = () => {};

const useNavigationHooks = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const search = searchParams.toString();

  const withSearch = (pathname: string) =>
    search ? `${pathname}?${search}` : pathname;

  return {
    toManagerOperatingSystem: () =>
      navigate(withSearch(MANAGER_OPERATING_SYSTEM_TEAM_DETAILS_PATH)),
    toManagerOperatingSystemTeamWorkflow: () =>
      navigate(withSearch(MANAGER_OPERATING_SYSTEM_TEAM_WORKFLOW_PATH)),
    toManagerOperatingSystemTeamDetails: () =>
      navigate(withSearch(MANAGER_OPERATING_SYSTEM_TEAM_DETAILS_PATH)),
    toManagerOperatingSystemOutcome: (outcomeId: string) =>
      navigate(
        withSearch(
          `${MANAGER_OPERATING_SYSTEM_OUTCOME_PATH_PREFIX}/${outcomeId}`
        )
      ),
    toManagerOperatingSystemGuide: (guideId: string) => {
      const next = setManagerOperatingSystemGuideSearchParam(
        searchParams,
        guideId
      );
      const search = next.toString();
      navigate(
        search
          ? `${MANAGER_OPERATING_SYSTEM_TEAM_DETAILS_PATH}?${search}`
          : MANAGER_OPERATING_SYSTEM_TEAM_DETAILS_PATH
      );
    },
  };
};

/** Route and navigation configuration for the Manager Operating System. */
export const managerOperatingSystemRouteConfig = {
  routes,
  useSyncHook,
  hideNavigationHeader: true,
  useNavigationHooks,
  publicRoute: false,
};
